/**
 * CDP SDK Session Management
 * Session tracking with timeout-based expiration
 */

import type { SessionData } from './types';
import { STORAGE_KEYS, DEFAULT_CONFIG } from './types';
import { generateId, log, getNow, getUTMParams } from './utils';
import { getStorage, setStorage, removeStorage, getStorageString, setStorageString } from './storage';

// =============================================================================
// SESSION MANAGEMENT
// =============================================================================

// The session also lives in memory: before consent the persistence gate keeps it
// off the device (СТ22), and every reader below must still see this page's
// session (a null one crashed createPageEvent).
let memorySession: SessionData | null = null;

function readSession(): SessionData | null {
  return getStorage<SessionData>(STORAGE_KEYS.SESSION) ?? memorySession;
}

function writeSession(session: SessionData): void {
  memorySession = session;
  setStorage(STORAGE_KEYS.SESSION, session);
}

/**
 * Get or create a session
 * Sessions expire after inactivity (default: 30 minutes)
 */
export function getOrCreateSession(
  sessionTimeout: number = DEFAULT_CONFIG.sessionTimeout!
): SessionData {
  const now = getNow();
  const stored = readSession();

  // Refresh email-click attribution from the URL on every page load. A
  // returning visitor who clicks ANOTHER email overwrites the prior
  // _pe (last-touch). If they navigate without a fresh _pe we keep
  // whatever was previously stored.
  const peFromUrl = readEmailClickIdFromUrl();
  if (peFromUrl) {
    setStorageString(STORAGE_KEYS.EMAIL_CLICK_ID, peFromUrl);
  }
  const stickyEmailClickId = getStorageString(STORAGE_KEYS.EMAIL_CLICK_ID);

  // Check if existing session is still valid
  if (stored && (now - stored.lastActivity) < sessionTimeout) {
    // Update existing session
    const updated: SessionData = {
      ...stored,
      lastActivity: now,
      pageViews: stored.pageViews + 1,
      emailClickId: peFromUrl || stored.emailClickId || stickyEmailClickId || null,
    };
    writeSession(updated);
    log('Session continued:', updated.id, 'pageViews:', updated.pageViews);
    return updated;
  }

  // Create new session
  const utmParams = getUTMParams(window.location.search);
  const newSession: SessionData = {
    id: generateId(),
    start: now,
    lastActivity: now,
    pageViews: 1,
    referrer: document.referrer || '',
    landingPage: window.location.href,
    utmSource: utmParams.source,
    utmMedium: utmParams.medium,
    utmCampaign: utmParams.campaign,
    utmTerm: utmParams.term,
    utmContent: utmParams.content,
    emailClickId: peFromUrl || stickyEmailClickId || null,
  };

  writeSession(newSession);
  log('New session started:', newSession.id);
  return newSession;
}

/**
 * Read `?_pe=<id>` from the current URL. push-tracking appends this
 * to the destination URL on every email click; the SDK promotes it
 * to a sticky property on every subsequent event so the attribution
 * chain (email click → page → product view → order completed) is
 * unbroken.
 */
function readEmailClickIdFromUrl(): string | null {
  if (typeof window === 'undefined' || !window.location) return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const pe = params.get('_pe');
    return pe && pe.length <= 64 ? pe : null;
  } catch {
    return null;
  }
}

/**
 * Read the long-lived sticky email_click_id (set by getOrCreateSession
 * when ?_pe=… is detected on any page). Survives session timeout so
 * orders placed days after the click still attribute correctly.
 */
export function getStickyEmailClickId(): string | null {
  return getStorageString(STORAGE_KEYS.EMAIL_CLICK_ID);
}

/**
 * Clear the sticky email_click_id. Call this after an attributed
 * Order Completed event if you want strict last-touch (next email
 * click = new attribution window) instead of always-attributable.
 */
export function clearStickyEmailClickId(): void {
  setStorageString(STORAGE_KEYS.EMAIL_CLICK_ID, '');
}

/**
 * Get the current session without creating one
 */
export function getSession(): SessionData | null {
  return readSession();
}

/**
 * Update the session's last activity timestamp
 * Call this on user interactions to keep session alive
 */
export function touchSession(): void {
  const session = readSession();
  if (session) {
    session.lastActivity = getNow();
    writeSession(session);
  }
}

/**
 * Increment the page view count for the current session
 */
export function incrementPageViews(): void {
  const session = readSession();
  if (session) {
    session.pageViews += 1;
    session.lastActivity = getNow();
    writeSession(session);
    log('Page views incremented:', session.pageViews);
  }
}

/**
 * Check if the current session is still valid
 */
export function isSessionValid(
  sessionTimeout: number = DEFAULT_CONFIG.sessionTimeout!
): boolean {
  const session = readSession();
  if (!session) {
    return false;
  }
  return (getNow() - session.lastActivity) < sessionTimeout;
}

/**
 * Get session duration in seconds
 */
export function getSessionDuration(): number {
  const session = readSession();
  if (!session) {
    return 0;
  }
  return Math.round((getNow() - session.start) / 1000);
}

/**
 * Get time since last activity in seconds
 */
export function getTimeSinceLastActivity(): number {
  const session = readSession();
  if (!session) {
    return Infinity;
  }
  return Math.round((getNow() - session.lastActivity) / 1000);
}

/**
 * Clear the current session
 */
export function clearSession(): void {
  memorySession = null;
  removeStorage(STORAGE_KEYS.SESSION);
  log('Session cleared');
}

/**
 * Force start a new session (even if current one is valid)
 */
export function startNewSession(): SessionData {
  clearSession();
  return getOrCreateSession();
}

// =============================================================================
// SESSION CONTEXT BUILDER
// =============================================================================

/**
 * Build session context for event payload
 */
export function buildSessionContext(session: SessionData): {
  id: string;
  pageViews: number;
  start: number;
  lastActivity: number;
  referrer?: string;
  landingPage?: string;
} {
  return {
    id: session.id,
    pageViews: session.pageViews,
    start: session.start,
    lastActivity: session.lastActivity,
    referrer: session.referrer || undefined,
    landingPage: session.landingPage || undefined,
  };
}

/**
 * Build campaign context from session UTM params
 */
export function buildCampaignContext(session: SessionData): {
  name?: string | null;
  source?: string | null;
  medium?: string | null;
  term?: string | null;
  content?: string | null;
} | undefined {
  // Return undefined if no UTM params
  if (
    !session.utmSource &&
    !session.utmMedium &&
    !session.utmCampaign &&
    !session.utmTerm &&
    !session.utmContent
  ) {
    return undefined;
  }

  return {
    name: session.utmCampaign,
    source: session.utmSource,
    medium: session.utmMedium,
    term: session.utmTerm,
    content: session.utmContent,
  };
}
